// components/AddMotionModal.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";
import { THEME_COLORS } from "../../../../context/constants";
import { MotionType } from "../../../../context/types";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: "10px",
    minWidth: "450px",
  },
  "& .MuiBackdrop-root": {
    position: "relative",
  },
}));

interface AddMotionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (avatarId: string, motionType: MotionType, prompt?: string) => Promise<void>;
  avatarName: string;
  avatarId: string;
  isLoading: boolean;
}

const MOTION_TYPE_OPTIONS: { value: MotionType; label: string; description: string }[] = [
  { value: "expressive", label: "Expressive", description: "Dynamic, emotionally rich animations" },
  { value: "consistent", label: "Consistent", description: "Stable, natural movements" },
  { value: "consistent_gen_3", label: "Consistent Gen 3", description: "Latest generation of consistent motion" },
  { value: "hailuo_2", label: "Hailuo 2", description: "Advanced motion synthesis" },
  { value: "veo2", label: "Veo 2", description: "High-quality video motion" },
  { value: "seedance_lite", label: "Seedance Lite", description: "Lightweight dance animations" },
  { value: "kling", label: "Kling", description: "Versatile motion generation" },
];

const AddMotionModal: React.FC<AddMotionModalProps> = ({
  open,
  onClose,
  onSubmit,
  avatarName,
  avatarId,
  isLoading,
}) => {
  const [motionType, setMotionType] = useState<MotionType>("expressive");
  const [prompt, setPrompt] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await onSubmit(avatarId, motionType, prompt || undefined);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setMotionType("expressive");
      setPrompt("");
    }
  };

  const selectedMotionInfo = MOTION_TYPE_OPTIONS.find(opt => opt.value === motionType);

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Motion to Avatar</Typography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            size="small"
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography
              variant="body2"
              color="black"
              sx={{ fontSize: "12px" }}
            >
              Add motion capabilities to <strong>{avatarName}</strong>. Select a motion type and optionally provide a prompt describing the desired animation.
            </Typography>

            <FormControl fullWidth required>
              <InputLabel
                id="motion-type-label"
                sx={{
                  color: THEME_COLORS.black,
                  "&.Mui-focused": {
                    color: THEME_COLORS.yellow,
                  },
                }}
              >
                Motion Type
              </InputLabel>
              <Select
                labelId="motion-type-label"
                value={motionType}
                label="Motion Type"
                onChange={(e) => setMotionType(e.target.value as MotionType)}
                disabled={isLoading}
              >
                {MOTION_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {selectedMotionInfo && (
                <FormHelperText>{selectedMotionInfo.description}</FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Motion Prompt (Optional)"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isLoading}
              multiline
              rows={3}
              placeholder="e.g., 'Nodding gently while maintaining eye contact', 'Pointing finger downward as if directing to a link below', 'Looking around with curiosity then smiling at the camera'"
              sx={{
                "& .MuiInputLabel-root": {
                  color: THEME_COLORS.black,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: THEME_COLORS.yellow,
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "20px" }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!motionType || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "Adding Motion..." : "Add Motion"}
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default AddMotionModal;
